import { useMemo, useState } from 'react';

import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import PushScreen from '@/designs-dev/device/PushScreen';
import Card from '@/components/wrapped/Card';
import { type Item } from '@/components/wrapped/ActionCard';
import ActionDetail from '@/north-star/actions/ActionDetail';
import CallSummary from '@/north-star/care/CallSummary';
import DocRow from '@/north-star/care/DocRow';
import { DocActionsSheet, EditDocSheet } from '@/north-star/care/DocSheets';
import {
  CALLS,
  DOCS,
  type CallItem,
  type DocItem,
} from '@/north-star/care/fixtures';
import CareSearchContent from '@/north-star/care/shortlists/CareSearchContent';
import ShortlistView from '@/north-star/care/shortlists/ShortlistView';
import ProviderDetail from '@/north-star/care/shortlists/ProviderDetail';
import {
  SHORTLISTS,
  type ProviderStatus,
  type ShortlistProvider,
} from '@/north-star/care/shortlists/fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// The Care tab for North Star — brought over from the member-app prototype and
// reduced to Calls / Docs / Shortlists (forms and tools dropped). Body-only:
// the shell owns the status bar and the docked-orb nav. Calls drill into
// summaries (and their linked actions); docs get a kebab sheet + edit drawer;
// shortlists open the published list → provider detail.

const SCREEN_PX = 2; // 16px gutter

const frostedFooter = {
  backgroundColor: (theme: any) =>
    alpha(theme.palette.background.default, 0.72),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

type CareTab = 'calls' | 'docs' | 'shortlists';

const CARE_SEGMENTS: { id: CareTab; label: string }[] = [
  { id: 'calls', label: 'Calls' },
  { id: 'docs', label: 'Docs' },
  { id: 'shortlists', label: 'Shortlists' },
];

const CALL_SECTIONS: { id: CallItem['status']; label: string }[] = [
  { id: 'upcoming', label: 'UPCOMING' },
  { id: 'past', label: 'PAST' },
];

const NorthStarCare = () => {
  const [tab, setTab] = useState<CareTab>('calls');
  const [calls, setCalls] = useState<CallItem[]>(CALLS);
  const [docs, setDocs] = useState<DocItem[]>(DOCS);

  const [openCallId, setOpenCallId] = useState<number | null>(null);
  const [actionDetail, setActionDetail] = useState<Item | null>(null);
  const [docMenu, setDocMenu] = useState<DocItem | null>(null);
  const [editingDoc, setEditingDoc] = useState<DocItem | null>(null);

  // Shortlist viewing: list → provider, tracked by id so the pushed views
  // reflect live secured-status changes while open.
  const [openShortlistId, setOpenShortlistId] = useState<string | null>(null);
  const [openProviderId, setOpenProviderId] = useState<string | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, ProviderStatus>
  >({});
  const [prevStatus, setPrevStatus] = useState<Record<string, ProviderStatus>>(
    {},
  );

  const resolvedShortlists = useMemo(
    () =>
      SHORTLISTS.map((sl) => ({
        ...sl,
        providers: sl.providers.map((p) => ({
          ...p,
          status: statusOverrides[p.id] ?? p.status,
        })),
      })),
    [statusOverrides],
  );
  const openShortlist =
    resolvedShortlists.find((s) => s.id === openShortlistId) ?? null;
  const openProvider =
    resolvedShortlists
      .flatMap((s) => s.providers)
      .find((p) => p.id === openProviderId) ?? null;

  const toggleSecured = (provider: ShortlistProvider) => {
    const { id } = provider;
    if (provider.status === 'Secured') {
      const revert = prevStatus[id] ?? 'Contacted';
      setStatusOverrides((o) => ({ ...o, [id]: revert }));
    } else {
      setPrevStatus((p) => ({ ...p, [id]: provider.status }));
      setStatusOverrides((o) => ({ ...o, [id]: 'Secured' }));
    }
  };

  const openCall = calls.find((c) => c.id === openCallId) ?? null;

  const setActionDone = (id: number, done: boolean) =>
    setCalls((prev) =>
      prev.map((c) => ({
        ...c,
        linkedActions: c.linkedActions.map((a) =>
          a.id === id ? { ...a, done } : a,
        ),
      })),
    );
  const toggleAction = (id: number) => {
    const current = openCall?.linkedActions.find((a) => a.id === id);
    setActionDone(id, !current?.done);
  };

  const uploadDoc = () => {
    const id = Date.now();
    setDocs((prev) => [
      { id, name: 'example_file.pdf', uploading: true },
      ...prev,
    ]);
    setTimeout(() => {
      setDocs((prev) =>
        prev.map((d) => (d.id === id ? { ...d, uploading: false } : d)),
      );
    }, 1800);
  };
  const deleteDoc = (id: number) =>
    setDocs((prev) => prev.filter((d) => d.id !== id));
  const renameDoc = (id: number, name: string) =>
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, name } : d)));

  const upcoming = calls.filter((c) => c.status === 'upcoming');
  const past = calls.filter((c) => c.status === 'past');

  const cta =
    tab === 'calls' && calls.length > 0
      ? { label: 'Book a call' as const, onPress: undefined }
      : tab === 'docs' && docs.length > 0
        ? { label: 'Upload document' as const, onPress: uploadDoc }
        : null;

  const emptyState = (title: string, button: string, onPress?: () => void) => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: SCREEN_PX,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <PageHeader title={title} isCentered />
      </Box>
      <Box sx={{ width: '100%' }}>
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel={button}
          onPress={onPress}
        >
          {button}
        </Button>
      </Box>
    </Box>
  );

  return (
    <ScreenRoot relative>
      <Box sx={{ px: SCREEN_PX, pt: 2, flexShrink: 0 }}>
        <ToggleButtonGroup
          className='segmented'
          exclusive
          fullWidth
          value={tab}
          onChange={(_, v) => v != null && setTab(v)}
        >
          {CARE_SEGMENTS.map((s) => (
            <ToggleButton key={s.id} value={s.id}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {tab === 'calls' && calls.length === 0 ? (
        emptyState('No calls yet', 'Book a call')
      ) : tab === 'docs' && docs.length === 0 ? (
        emptyState('No docs yet', 'Upload document', uploadDoc)
      ) : (
        <Box
          sx={{ flex: 1, overflow: 'auto', px: SCREEN_PX, pt: 2, pb: '96px' }}
        >
          {tab === 'calls' && (
            <Stack spacing={1}>
              {CALL_SECTIONS.map((section) => {
                const items = section.id === 'upcoming' ? upcoming : past;
                if (items.length === 0) return null;
                return (
                  <Box key={section.id}>
                    <Typography
                      variant='overline'
                      color='text.secondary'
                      sx={{ display: 'block', mt: 1, mb: 1 }}
                    >
                      {section.label}
                    </Typography>
                    <Stack spacing={1.5}>
                      {items.map((c) => (
                        <Card
                          key={c.id}
                          variant='compact'
                          title={c.title}
                          subtitle={[
                            `${c.when} · with ${c.withName}`,
                            `From ${c.from}`,
                          ]}
                          onClick={() => setOpenCallId(c.id)}
                        />
                      ))}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}

          {tab === 'docs' && (
            <Stack spacing={1.5}>
              {docs.map((d) => (
                <DocRow key={d.id} doc={d} onMenu={setDocMenu} />
              ))}
            </Stack>
          )}

          {tab === 'shortlists' && (
            <CareSearchContent
              shortlists={resolvedShortlists}
              onOpen={(s) => setOpenShortlistId(s.id)}
            />
          )}
        </Box>
      )}

      {cta && (
        <Box
          sx={[
            {
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              px: SCREEN_PX,
              py: 1.5,
            },
            frostedFooter,
          ]}
        >
          <Button
            variant='primary'
            size='lg'
            isFullWidth
            ariaLabel={cta.label}
            onPress={cta.onPress}
          >
            {cta.label}
          </Button>
        </Box>
      )}

      {/* pushed stack pages + sheets — portal over the whole phone */}
      {openCall && (
        <PushScreen onClose={() => setOpenCallId(null)}>
          {(close) => (
            <CallSummary
              call={openCall}
              onBack={close}
              onToggleAction={toggleAction}
              onOpenAction={setActionDetail}
            />
          )}
        </PushScreen>
      )}
      {openShortlist && (
        <PushScreen onClose={() => setOpenShortlistId(null)}>
          {(close) => (
            <ShortlistView
              shortlist={openShortlist}
              onBack={close}
              onOpenProvider={(p) => setOpenProviderId(p.id)}
              onToggleSecured={toggleSecured}
            />
          )}
        </PushScreen>
      )}
      {openProvider && (
        <ProviderDetail
          asSheet
          provider={openProvider}
          onBack={() => setOpenProviderId(null)}
          onToggleSecured={toggleSecured}
        />
      )}
      {actionDetail && (
        <ActionDetail
          action={actionDetail}
          onClose={() => setActionDetail(null)}
          onSetDone={setActionDone}
          resolveLinkedCall={(linked) =>
            calls.find((c) => c.when === linked.when) ?? null
          }
        />
      )}
      {docMenu && (
        <DocActionsSheet
          onView={() => setDocMenu(null)}
          onEdit={() => {
            setEditingDoc(docMenu);
            setDocMenu(null);
          }}
          onDelete={() => {
            deleteDoc(docMenu.id);
            setDocMenu(null);
          }}
          onClose={() => setDocMenu(null)}
        />
      )}
      {editingDoc && (
        <EditDocSheet
          doc={editingDoc}
          onSave={(name) => {
            renameDoc(editingDoc.id, name);
            setEditingDoc(null);
          }}
          onClose={() => setEditingDoc(null)}
        />
      )}
    </ScreenRoot>
  );
};

NorthStarCare.displayName = 'NorthStarCare';
export default NorthStarCare;
